import React from "react";

export interface CriticalBandPillProps {
  /** Icon glyph rendered inside the colored disc (e.g. "☀"). */
  icon: string;
  /** Visible title text (e.g. "Solar Hours"). */
  label: string;
  /** Time range text (e.g. "10:00–16:00"). */
  time: string;
  /**
   * Icon disc style variant. Matches the `cp-band-icon--<kind>` CSS classes:
   * "solar" | "evening_ramp" | "valley" | "peak_demand".
   */
  iconKind: string;
  /** Accent color used by the pill border/shadow via the --cp-accent CSS var. */
  color: string;
  /** Icon-only mode (hides the text block). */
  compact?: boolean;
  /** Disable the entrance animation. */
  noAnimation?: boolean;
  /** Extra inline styles merged onto the pill root. */
  style?: React.CSSProperties;
  /** Extra class names appended to the pill root. */
  className?: string;
  /** Native title attribute (hover tooltip). */
  title?: string;
}

/**
 * Single critical-period pill — shared by the chart overlay (CriticalBandLabels)
 * and the data table so both render an identical UI. Relies on the `cp-band-*`
 * styles in ReMixChart.css.
 */
export function CriticalBandPill({
  icon,
  label,
  time,
  iconKind,
  color,
  compact = false,
  noAnimation = false,
  style,
  className = "",
  title,
}: CriticalBandPillProps) {
  return (
    <span
      className={`cp-band-label ${compact ? "cp-band-label--compact" : ""} ${className}`.trim()}
      style={{
        ["--cp-accent" as any]: color,
        ...(noAnimation ? { animation: "none" } : {}),
        ...style,
      } as React.CSSProperties}
      title={title}
    >
      <span className={`cp-band-icon cp-band-icon--${iconKind}`}>
        <span className="cp-band-icon-glyph">{icon}</span>
      </span>
      {!compact && (
        <span className="cp-band-text">
          <span className="cp-band-title">{label}</span>
          <span className="cp-band-sep">·</span>
          <span className="cp-band-time">{time}</span>
        </span>
      )}
    </span>
  );
}
